package com.havn.app.features.reports.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.core.supabase.havnSlowSqlDetail
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseRepository
import com.havn.app.features.reports.domain.SellerReport
import com.havn.app.features.reports.domain.SellerReportAiInput
import com.havn.app.features.reports.domain.SellerReportAiOpenHouse
import com.havn.app.features.reports.domain.SellerReportAiService
import com.havn.app.features.reports.domain.SellerReportAiTotals
import com.havn.app.features.reports.domain.SellerReportDraft
import com.havn.app.features.reports.domain.SellerReportPreview
import com.havn.app.features.reports.domain.SellerReportRepository
import com.havn.app.features.reports.domain.SellerReportStatus
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.cancel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.time.Clock

class SellerReportsViewModel(
    private val sellerReportRepository: SellerReportRepository,
    private val sellerReportAiService: SellerReportAiService,
    private val openHouseRepository: OpenHouseRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(SellerReportsUiState())
    val uiState: StateFlow<SellerReportsUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var selectedAgencyId: String? = null
    private var actAsAgent = false
    private var hasLoadedAgencyContext: Boolean = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val loggedIn = state as? AuthState.LoggedIn
                agentId = loggedIn?.user?.id
                if (loggedIn == null) {
                    _uiState.value = SellerReportsUiState()
                } else {
                    openHouseRepository.ensureAgentProfile(loggedIn.user.id, loggedIn.user.email)
                        .onFailure { setError(it) }
                    loadData(loggedIn.user.id)
                }
            }
        }
    }

    fun setAgencyContext(agencyId: String?, nextActAsAgent: Boolean = false) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId && actAsAgent == nextActAsAgent) return
        selectedAgencyId = agencyId
        actAsAgent = nextActAsAgent
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch { loadData(currentAgentId) }
    }

    fun openGenerator() {
        _uiState.value = _uiState.value.copy(mode = SellerReportsMode.Generate, error = null)
    }

    fun openList() {
        _uiState.value = _uiState.value.copy(mode = SellerReportsMode.List, selectedReportId = null, error = null)
        refresh()
    }

    fun openReport(reportId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            isLoading = true,
            selectedReportId = reportId,
            preview = null,
            savedReportId = null,
            mode = SellerReportsMode.Preview,
            error = null
        )
        scope.launch {
            sellerReportRepository.previewForReport(currentAgentId, reportId)
                .onSuccess { preview ->
                    _uiState.value = _uiState.value.copy(
                        isLoading = false,
                        preview = preview,
                        savedReportId = preview.reportId,
                        mode = SellerReportsMode.Preview
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isLoading = false, selectedReportId = null)
                    setError(it)
                }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadData(currentAgentId) }
    }

    fun selectOpenHouse(openHouseId: String) {
        _uiState.value = _uiState.value.copy(selectedOpenHouseId = openHouseId, error = null)
    }

    fun updateAgentNotes(notes: String) {
        _uiState.value = _uiState.value.copy(agentNotes = notes, error = null)
    }

    fun updatePreviewSummary(summary: String) {
        _uiState.value = _uiState.value.copy(
            preview = _uiState.value.preview?.copy(summaryText = summary)
        )
    }

    fun updatePreviewRecommendations(recommendations: String) {
        _uiState.value = _uiState.value.copy(
            preview = _uiState.value.preview?.copy(recommendations = recommendations)
        )
    }

    fun updatePreviewNextSteps(nextSteps: String) {
        _uiState.value = _uiState.value.copy(
            preview = _uiState.value.preview?.copy(nextSteps = nextSteps)
        )
    }

    fun updatePreviewStatus(status: SellerReportStatus) {
        val currentAgentId = agentId
        val preview = _uiState.value.preview
        _uiState.value = _uiState.value.copy(
            preview = preview?.copy(status = status),
            error = null
        )
        val reportId = preview?.reportId ?: return
        if (currentAgentId == null) return
        scope.launch {
            sellerReportRepository.updateReport(
                agentId = currentAgentId,
                reportId = reportId,
                status = status,
                summary = preview.summaryText,
                sellerFeedback = preview.agentNotes,
                nextSteps = preview.nextSteps
            ).onSuccess { report ->
                val refreshedReports = sellerReportRepository.reportsForAgent(currentAgentId, selectedAgencyId, actAsAgent).getOrNull()
                _uiState.value = _uiState.value.copy(
                    reports = refreshedReports
                        ?: listOf(report) + _uiState.value.reports.filterNot { it.id == report.id },
                    preview = _uiState.value.preview?.copy(status = report.status)
                )
            }.onFailure {
                setError(it)
            }
        }
    }

    fun generatePreview() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId
        if (openHouseId == null) {
            _uiState.value = _uiState.value.copy(error = "Choose an open house before generating a seller report.")
            return
        }

        _uiState.value = _uiState.value.copy(isGenerating = true, error = null)
        scope.launch {
            sellerReportRepository.buildPreview(currentAgentId, openHouseId, _uiState.value.agentNotes)
                .onSuccess { preview ->
                    _uiState.value = _uiState.value.copy(
                        isGenerating = false,
                        preview = preview,
                        mode = SellerReportsMode.Preview
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isGenerating = false)
                    setError(it)
                }
        }
    }

    fun generatePropertyReport(propertyId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            isGenerating = true,
            generatingPropertyId = propertyId,
            mode = SellerReportsMode.Preview,
            preview = null,
            error = null,
            savedReportId = null
        )
        scope.launch {
            sellerReportRepository.createPropertyPerformanceDraft(
                agentId = currentAgentId,
                propertyId = propertyId
            ).onSuccess { preview ->
                val refreshedReports = sellerReportRepository.reportsForAgent(currentAgentId, selectedAgencyId, actAsAgent).getOrNull()
                _uiState.value = _uiState.value.copy(
                    isGenerating = false,
                    generatingPropertyId = null,
                    reports = refreshedReports ?: _uiState.value.reports,
                    preview = preview,
                    savedReportId = preview.reportId,
                    mode = SellerReportsMode.Preview
                )
            }.onFailure {
                _uiState.value = _uiState.value.copy(
                    isGenerating = false,
                    generatingPropertyId = null
                )
                setError(it)
            }
        }
    }

    fun generateAiInsights(language: String) {
        val preview = _uiState.value.preview ?: return
        val currentAgentId = agentId ?: return
        if (preview.propertyId == null) {
            _uiState.value = _uiState.value.copy(error = "AI insights need a property-linked seller report.")
            return
        }
        val requestId = havnAiRequestId()
        val startedAtMs = Clock.System.now().toEpochMilliseconds()
        HavnLogger.info(
            "SellerReportsAI",
            "[$requestId] start property=${preview.propertyId} report=${preview.reportId ?: "draft"} language=$language openHouses=${preview.timeline.size} visitors=${preview.totalVisitors} notes=${preview.agentNotes.length}"
        )
        _uiState.value = _uiState.value.copy(isGenerating = true, error = null)
        scope.launch {
            sellerReportAiService.generateInsights(preview.toAiInput(language))
                .onSuccess { aiJson ->
                    val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
                    HavnLogger.info("SellerReportsAI", "[$requestId] success elapsed=${elapsedMs}ms responseChars=${aiJson.length}")
                    val reportId = _uiState.value.preview?.reportId
                    _uiState.value = _uiState.value.copy(
                        isGenerating = false,
                        preview = _uiState.value.preview?.copy(aiInsightsJson = aiJson),
                        mode = SellerReportsMode.Preview
                    )
                    if (reportId != null) {
                        sellerReportRepository.updateReportAiInsights(
                            agentId = currentAgentId,
                            reportId = reportId,
                            aiInsightsJson = aiJson
                        ).onFailure { error ->
                            HavnLogger.error("SellerReportsAI", "[$requestId] failed to persist AI insights", error)
                        }
                    }
                }.onFailure {
                    val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
                    HavnLogger.error("SellerReportsAI", "[$requestId] failed elapsed=${elapsedMs}ms", it)
                    _uiState.value = _uiState.value.copy(isGenerating = false)
                    setError(it)
                }
        }
    }

    fun deletePreviewReport() {
        val currentAgentId = agentId ?: return
        val reportId = _uiState.value.preview?.reportId ?: return
        _uiState.value = _uiState.value.copy(isDeletingReport = true, error = null)
        scope.launch {
            sellerReportRepository.deleteReport(currentAgentId, reportId)
                .onSuccess {
                    val refreshedReports = sellerReportRepository.reportsForAgent(currentAgentId, selectedAgencyId, actAsAgent).getOrNull()
                    _uiState.value = _uiState.value.copy(
                        isDeletingReport = false,
                        reports = refreshedReports ?: _uiState.value.reports.filterNot { it.id == reportId },
                        preview = null,
                        savedReportId = null,
                        selectedReportId = null,
                        mode = SellerReportsMode.List
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDeletingReport = false)
                    setError(it)
                }
        }
    }

    fun savePreview() {
        val currentAgentId = agentId ?: return
        val preview = _uiState.value.preview ?: return
        val reportId = preview.reportId
        if (reportId != null) {
            _uiState.value = _uiState.value.copy(isSaving = true, error = null)
            scope.launch {
                sellerReportRepository.updateReport(
                    agentId = currentAgentId,
                    reportId = reportId,
                    status = preview.status,
                    summary = preview.summaryText,
                    sellerFeedback = preview.agentNotes,
                    nextSteps = preview.nextSteps
                ).onSuccess { report ->
                    val refreshedReports = sellerReportRepository.reportsForAgent(currentAgentId, selectedAgencyId, actAsAgent).getOrNull()
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        reports = refreshedReports
                            ?: listOf(report) + _uiState.value.reports.filterNot { it.id == report.id },
                        preview = preview.copy(status = report.status),
                        savedReportId = report.id
                    )
                }.onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
            }
            return
        }

        val openHouseId = preview.openHouseId
        if (openHouseId == null) {
            _uiState.value = _uiState.value.copy(savedReportId = preview.reportId)
            return
        }
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            sellerReportRepository.saveReport(
                agentId = currentAgentId,
                draft = SellerReportDraft(
                    openHouseId = openHouseId,
                    visitorCount = preview.totalVisitors,
                    hotCount = preview.hotLeads,
                    warmCount = preview.warmLeads,
                    coldCount = preview.coldLeads,
                    summary = preview.summaryText,
                    sellerFeedback = preview.agentNotes,
                    nextSteps = preview.nextSteps.takeIf { it.isNotBlank() }
                        ?: "Continue timely follow-up with hot and warm leads. Revisit pricing, positioning, or showing strategy if qualified activity remains low.",
                    status = preview.status
            )
            ).onSuccess { report ->
                val refreshedReports = sellerReportRepository.reportsForAgent(currentAgentId, selectedAgencyId, actAsAgent).getOrNull()
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    reports = refreshedReports
                        ?: listOf(report) + _uiState.value.reports.filterNot { it.id == report.id },
                    preview = preview.copy(reportId = report.id, status = report.status),
                    savedReportId = report.id
                )
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false)
                setError(it)
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun loadData(currentAgentId: String) {
        _uiState.value = _uiState.value.copy(isLoading = true, error = null)
        val currentAgencyId = selectedAgencyId
        havnSlowSqlDetail("sellerReports.refresh(agent_id=$currentAgentId, agency_id=${currentAgencyId ?: "personal"})") {
            val (openHouses, reports) = coroutineScope {
                val openHousesDeferred = async {
                    havnSlowSqlDetail("sellerReports.refresh.openHouses(agent_id=$currentAgentId, agency_id=${currentAgencyId ?: "personal"})") {
                        openHouseRepository.openHousesForAgent(currentAgentId, currentAgencyId, actAsAgent)
                    }
                }
                val reportsDeferred = async {
                    havnSlowSqlDetail("sellerReports.refresh.reports(agent_id=$currentAgentId, agency_id=${currentAgencyId ?: "personal"})") {
                        sellerReportRepository.reportsForAgent(currentAgentId, currentAgencyId, actAsAgent)
                    }
                }
                openHousesDeferred.await() to reportsDeferred.await()
            }

            if (openHouses.isFailure) {
                _uiState.value = _uiState.value.copy(isLoading = false)
                setError(openHouses.exceptionOrNull() ?: IllegalStateException("Unable to load open houses."))
            } else if (reports.isFailure) {
                _uiState.value = _uiState.value.copy(isLoading = false)
                setError(reports.exceptionOrNull() ?: IllegalStateException("Unable to load seller reports."))
            } else {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    openHouses = openHouses.getOrThrow(),
                    reports = reports.getOrThrow(),
                    selectedOpenHouseId = _uiState.value.selectedOpenHouseId
                        ?: openHouses.getOrThrow().firstOrNull()?.id
                )
            }
        }
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("SellerReportsViewModel", "User-visible seller reports error", error)
        _uiState.value = _uiState.value.copy(
            error = error.havnFriendlyMessage("Could not load seller reports. Please try again.")
        )
    }
}

private fun havnAiRequestId(): String =
    Clock.System.now().toEpochMilliseconds().toString()

private fun SellerReportPreview.toAiInput(language: String): SellerReportAiInput {
    val periodParts = reportPeriod.split(" - ").map { it.trim() }.filter { it.isNotBlank() }
    return SellerReportAiInput(
        language = language,
        propertyTitle = propertyTitle,
        propertyAddress = propertyAddress,
        propertyPrice = propertyPrice,
        propertyStatus = propertyStatus,
        daysOnMarket = daysActive,
        reportPeriodStart = periodParts.firstOrNull() ?: "Not available",
        reportPeriodEnd = periodParts.getOrNull(1) ?: periodParts.firstOrNull() ?: "Not available",
        openHouses = timeline.map {
            SellerReportAiOpenHouse(
                date = it.date,
                visitors = it.visitors,
                hotLeads = it.hotLeads,
                privateTours = it.privateTours,
                followUpsSent = it.followUpsSent
            )
        },
        totals = SellerReportAiTotals(
            visitors = totalVisitors,
            hotLeads = hotLeads,
            warmLeads = warmLeads,
            coldLeads = coldLeads,
            privateTours = privateTours,
            followUpsSent = followUpsSent
            ,propertyInquiries = inquiryCount
            ,leadSources = leadSources
        ),
        agentNotes = listOfNotNull(
            agentNotes.takeIf { it.isNotBlank() },
            mostLovedFeatures.takeIf { it.isNotBlank() },
            commonConcerns.takeIf { it.isNotBlank() },
            keyBuyerPatterns.takeIf { it.isNotBlank() }
        )
    )
}

data class SellerReportsUiState(
    val isLoading: Boolean = false,
    val isGenerating: Boolean = false,
    val isSaving: Boolean = false,
    val isDeletingReport: Boolean = false,
    val error: String? = null,
    val mode: SellerReportsMode = SellerReportsMode.List,
    val openHouses: List<OpenHouse> = emptyList(),
    val reports: List<SellerReport> = emptyList(),
    val selectedOpenHouseId: String? = null,
    val agentNotes: String = "",
    val preview: SellerReportPreview? = null,
    val savedReportId: String? = null,
    val generatingPropertyId: String? = null,
    val selectedReportId: String? = null
) {
    val selectedOpenHouse: OpenHouse?
        get() = openHouses.firstOrNull { it.id == selectedOpenHouseId }

    val totalVisitors: Int
        get() = reports.sumOf { it.visitorCount }

    val totalHotLeads: Int
        get() = reports.sumOf { it.hotCount }
}

enum class SellerReportsMode {
    List,
    Generate,
    Preview
}
